/**
 * 웹 푸시(FCM) + DB 설정(NOTIFY.ALL 모든 알림) 연동
 *
 * - 앱 실행: 이미 허용된 경우만 조용히 토큰 동기화 (권한창 안 뜸)
 * - 최초 1회: 알림 허용 제안 모달 (내정보에서도 변경 가능)
 * - 내정보 토글: 사용자가 켤 때만 기기 권한 요청
 */
import axios from 'axios'
import { initializeApp, getApps } from 'firebase/app'
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'
import appApi, { apiErrorMessage } from '@/services/appApi'
import {
  stashPendingNotify,
  consumePendingNotify,
  peekPendingNotify,
  isSafeNotifyPath,
} from '@/composables/useNotifyDeepLink'
import { APP_VERSION } from '@/config/appVersion'
import {
  clearPendingNotifyAfterInstall,
  detectInAppBrowser,
  getPushServiceWorkerRegistration,
  hasPendingNotifyAfterInstall,
  isStandaloneDisplay,
} from '@/composables/usePwaInstall'

/** 모든 알림 마스터 스위치 */
export const SETTING_NOTIFY_ALL = 'NOTIFY.ALL'

const TOKEN_KEY = 'caion_fcm_token'
/** 이 기기에서 최초 알림 제안을 이미 보여줬는지 */
const ONBOARD_KEY = 'caion_notify_onboarded'
/** 마지막으로 알림 온보딩을 처리한 앱 버전 */
const PROMPT_VERSION_KEY = 'caion_notify_prompt_version'

/** Android PWA 진동 — 삐링삐링 + 지잉 */
const PUSH_VIBRATE = [300, 120, 300, 120, 300, 120, 500]

/** 포그라운드 짧은 알림음 (삐-삐) */
const playAlertBeep = () => {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const beep = (at: number, freq: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, at)
      gain.gain.exponentialRampToValueAtTime(0.22, at + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(at)
      osc.stop(at + 0.18)
    }
    const t0 = ctx.currentTime
    beep(t0, 880)
    beep(t0 + 0.22, 1040)
    window.setTimeout(() => {
      try {
        ctx.close()
      } catch {
        /* ignore */
      }
    }, 800)
  } catch {
    /* ignore */
  }
}

const pulseDevice = () => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      // 이전 진동 끊고 패턴 재생 (Android Chrome)
      navigator.vibrate(0)
      navigator.vibrate(PUSH_VIBRATE)
    }
  } catch {
    /* ignore */
  }
  playAlertBeep()
}

const todayDateKey = () => {
  const t = new Date()
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
}

const toAppPath = (raw?: string | null): string => {
  const s = String(raw || '').trim()
  if (!s || s === 'undefined' || s === 'null') return ''
  if (s.startsWith('http')) {
    try {
      const u = new URL(s)
      const path = `${u.pathname}${u.search}${u.hash}`
      if (!path || path.includes('undefined')) return ''
      return path
    } catch {
      return ''
    }
  }
  const path = s.startsWith('/') ? s : `/${s}`
  if (path.includes('undefined')) return ''
  return path
}

const safeRouterPush = async (router: any, target: string | Record<string, any>) => {
  try {
    const { repairVueRouterHistoryState } = await import('@/composables/useAppBackNavigation')
    repairVueRouterHistoryState()
  } catch {
    /* ignore */
  }
  try {
    await router.push(target)
  } catch (e) {
    console.error('[push] router.push failed:', e)
    const path = typeof target === 'string' ? target : (target as any)?.path
    if (typeof path === 'string' && path.startsWith('/')) {
      window.location.assign(path)
    }
  }
}

/**
 * OS/포그라운드 알림 클릭 → 채팅방 또는 일정(당일 시트)으로 이동
 */
export const openNotificationTarget = async (
  rawLink?: string | null,
  data?: Record<string, any> | null
): Promise<void> => {
  try {
    const router = (await import('@/router')).default
    const { authStore } = await import('@/stores/auth')
    const d = data || {}
    const type = String(d.type || '')
    let link = toAppPath(rawLink || d.link)

    // 링크 없이 type·필드만 온 경우 경로 재구성
    if (!link || !isSafeNotifyPath(link)) {
      if (type === 'center_chat' || d.openChat || d.cayIdx || d.mCode) {
        const cay = String(d.openChat || d.cayIdx || '').trim()
        const mCode = String(d.mCode || '').trim()
        const role = authStore.state.user?.appRole
        if (mCode && (!role || role === 'CAREGIVER')) {
          const qName = d.centerName || d.name || ''
          link = `/caregiver/center/${encodeURIComponent(mCode)}/chat${
            qName ? `?name=${encodeURIComponent(String(qName))}` : ''
          }`
        } else if (cay && role === 'OWNER') {
          link = `/owner?openChat=${encodeURIComponent(cay)}`
        } else if (cay && role === 'SOCIAL_WORKER') {
          link = `/sw?openChat=${encodeURIComponent(cay)}`
        } else if (cay && !role) {
          // 로그인 전 — 역할 모름, cay 만 보관 (셸에서 openChat 처리)
          link = `/owner?openChat=${encodeURIComponent(cay)}`
          sessionStorage.setItem('caion_open_tab', 'chat')
        } else if (role === 'CAREGIVER') {
          sessionStorage.setItem('caion_open_tab', 'center')
          link = '/caregiver'
        }
      } else if (type === 'schedule_remind' || d.openSchedule === '1') {
        const date = String(d.date || todayDateKey())
        link = `/caregiver?openSchedule=1&date=${encodeURIComponent(date)}`
      }
    }

    if (link && isSafeNotifyPath(link)) {
      stashPendingNotify(link, d)
    }

    // 부팅·미로그인 중이면 보관만 하고 스플래시/셸이 소비
    if (authStore.state.booting || !authStore.state.user) {
      return
    }

    const isSchedule =
      type === 'schedule_remind' ||
      link.includes('openSchedule') ||
      String(d.openSchedule || '') === '1'

    if (isSchedule) {
      let date = String(d.date || '').trim()
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) && link) {
        try {
          date = new URL(link, window.location.origin).searchParams.get('date') || ''
        } catch {
          /* ignore */
        }
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) date = todayDateKey()
      sessionStorage.setItem('caion_schedule_focus_date', date)
      sessionStorage.setItem('caion_open_tab', 'schedule')
      consumePendingNotify()
      if (router.currentRoute.value.path === '/caregiver') {
        window.dispatchEvent(
          new CustomEvent('caion:open-schedule-today', { detail: { date } })
        )
      } else {
        await safeRouterPush(router, { path: '/caregiver', query: { openSchedule: '1', date } })
      }
      return
    }

    const isChat =
      type === 'center_chat' ||
      /\/chat(?:\?|$)/.test(link) ||
      /[?&]openChat=/.test(link)

    if (isChat && link) {
      // 센터장/사복: 이미 셸에 있으면 쿼리만 갱신해도 watch 가 방을 연다
      if (
        (link.startsWith('/owner') || link.startsWith('/sw')) &&
        /[?&]openChat=/.test(link)
      ) {
        sessionStorage.setItem('caion_open_tab', 'chat')
      }
      consumePendingNotify()
      await safeRouterPush(router, link)
      return
    }

    if (link && isSafeNotifyPath(link)) {
      consumePendingNotify()
      await safeRouterPush(router, link)
    }
  } catch (e) {
    console.error('[push] openNotificationTarget:', e)
  }
}

/** 스플래시·셸 마운트 후 보관된 알림 딥링크 실행 */
export const flushPendingNotifyTarget = async (): Promise<boolean> => {
  const pending = peekPendingNotify()
  if (!pending) return false
  await openNotificationTarget(pending.link, pending.data || {})
  return true
}

/** SW → 페이지 진동 / 알림 클릭 이동 (앱 부팅 직후 바인딩해야 클릭 유실 없음) */
let swBridgeBound = false
export const bindSwVibrateBridge = () => {
  if (swBridgeBound) return
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  swBridgeBound = true
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data
    if (!data) return
    if (data.type === 'CAION_VIBRATE') {
      pulseDevice()
      return
    }
    if (data.type === 'CAION_PUSH') {
      const d = data.data || {}
      if (d.type === 'center_chat') {
        window.dispatchEvent(new CustomEvent('caion:center-chat-push', { detail: d }))
      } else {
        window.dispatchEvent(new CustomEvent('caion:inbox-changed'))
      }
      return
    }
    if (data.type === 'CAION_NOTIFICATION_CLICK') {
      stashPendingNotify(data.link, data.data || {})
      void openNotificationTarget(data.link, data.data || {})
    }
  })
}

let messagingInstance: ReturnType<typeof getMessaging> | null = null
let registeredToken: string | null = null
let foregroundBound = false

const saveTokenLocal = (token: string) => {
  registeredToken = token
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* ignore */
  }
}

const readTokenLocal = (): string | null => {
  if (registeredToken) return registeredToken
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

const clearTokenLocal = () => {
  registeredToken = null
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export const isNotifyOnboarded = (): boolean => {
  try {
    return localStorage.getItem(ONBOARD_KEY) === '1'
  } catch {
    return false
  }
}

export const markNotifyOnboarded = () => {
  try {
    localStorage.setItem(ONBOARD_KEY, '1')
    localStorage.setItem(PROMPT_VERSION_KEY, APP_VERSION)
  } catch {
    /* ignore */
  }
}

/**
 * 앱 버전 변경 시 알림 제안 정책
 * - OS 권한이 아직 default 이면 「나중에」 기록을 지워 다시 물어봄
 * - 이미 granted/denied 이면 OS가 권한을 유지하므로 재질문하지 않음 (토큰만 동기화)
 */
const applyNotifyPromptVersionPolicy = (): 'update' | null => {
  try {
    const prev = localStorage.getItem(PROMPT_VERSION_KEY)
    if (prev === APP_VERSION) return null
    localStorage.setItem(PROMPT_VERSION_KEY, APP_VERSION)
    if (!prev) return null // 첫 기록 — 기존 온보딩 유지
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      localStorage.removeItem(ONBOARD_KEY)
      return 'update'
    }
  } catch {
    /* ignore */
  }
  return null
}

export type NotifyProposeReason = 'install' | 'update' | 'onboard'

/** 셸 확인창 문구 */
export const notifyProposeMessage = (
  reason?: NotifyProposeReason
): { title: string; message: string } => {
  if (reason === 'install') {
    return {
      title: '알림 설정',
      message:
        '앱 설치가 완료되었습니다.\n채팅·일정 알림을 받으려면 알림을 허용해 주세요.\n나중에 내정보에서 언제든 바꿀 수 있습니다.',
    }
  }
  if (reason === 'update') {
    return {
      title: '알림 설정 확인',
      message:
        '앱이 업데이트되었습니다.\n알림이 꺼져 있을 수 있으니, 알림을 허용해 주세요.\n나중에 내정보에서 언제든 바꿀 수 있습니다.',
    }
  }
  return {
    title: '알림 허용',
    message:
      '중요한 알림을 받으려면 알림을 허용해 주세요.\n나중에 내정보에서 언제든 바꿀 수 있습니다.',
  }
}

const loadConfig = async () => {
  const res = await appApi.get('/push/config')
  if (!res.success || !res.data?.enabled) return null
  return res.data as {
    apiKey: string
    authDomain: string
    projectId: string
    messagingSenderId: string
    appId: string
    vapidKey: string
  }
}

/** Firebase 초기화 + 포그라운드 onMessage (토큰 등록 성공 여부와 무관) */
export const initPushListeners = async (): Promise<void> => {
  try {
    if (typeof window === 'undefined') return
    bindSwVibrateBridge()
    bindNotifyPermissionWatch()
    if (!(await isSupported())) return
    const cfg = await loadConfig()
    if (!cfg) return
    if (!getApps().length) {
      initializeApp({
        apiKey: cfg.apiKey,
        authDomain: cfg.authDomain,
        projectId: cfg.projectId,
        messagingSenderId: cfg.messagingSenderId,
        appId: cfg.appId,
      })
    }
    messagingInstance = getMessaging()
    bindForeground()
  } catch (e) {
    console.error('[push] initPushListeners:', e)
  }
}

const bindForeground = () => {
  if (!messagingInstance || foregroundBound) return
  foregroundBound = true
  onMessage(messagingInstance, (payload) => {
    const title = payload.notification?.title || payload.data?.title || '카이온 알림'
    const body = payload.notification?.body || payload.data?.body || '새 알림이 있습니다.'
    const isChat = payload.data?.type === 'center_chat'
    const msgTag =
      payload.data?.tag ||
      (isChat
        ? `cchat-fg-${payload.data?.messageIdx || Date.now()}`
        : payload.data?.casrIdx
          ? `casr-${payload.data.casrIdx}`
          : 'caion-notify')

    if (isChat) {
      window.dispatchEvent(new CustomEvent('caion:center-chat-push', { detail: payload.data }))
    } else {
      window.dispatchEvent(new CustomEvent('caion:inbox-changed'))
    }

    pulseDevice()

    // 앱이 떠 있으면 OS가 notification 페이로드를 안 띄움 → 여기서 반드시 표시
    // (화면만 켜져 있고 백그라운드로 착각하는 경우 포함)
    void showForegroundNotification(title, body, {
      tag: msgTag,
      link: payload.data?.link || '/caregiver',
      data: payload.data || {},
      isChat,
    })
  })
}

/** 포그라운드 알림 — SW showNotification 우선(진동·소리), 실패 시 Notification API */
const showForegroundNotification = async (
  title: string,
  body: string,
  opts: { tag: string; link: string; isChat: boolean; data?: Record<string, any> }
) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  const origin = window.location.origin
  const notifyData = {
    ...(opts.data || {}),
    link: opts.link,
    type: opts.data?.type || (opts.isChat ? 'center_chat' : 'notify'),
  }
  const options: NotificationOptions = {
    body,
    icon: `${origin}/pwa-icons/caion-any-192.png?v=20261028b`,
    badge: `${origin}/pwa-icons/caion-monochrome-192.png?v=20261028b`,
    tag: opts.tag,
    renotify: true,
    silent: false,
    vibrate: PUSH_VIBRATE,
    data: notifyData,
  }
  try {
    const reg = await getPushServiceWorkerRegistration()
    if (reg?.showNotification) {
      await reg.showNotification(title, options)
      return
    }
  } catch (e) {
    console.warn('[push] SW showNotification 실패, Notification API 사용:', e)
  }
  try {
    const n = new Notification(title, options)
    n.onclick = () => {
      try {
        window.focus()
      } catch {
        /* ignore */
      }
      void openNotificationTarget(opts.link, notifyData)
      n.close()
    }
  } catch (e) {
    console.warn('[push] Notification 표시 실패:', e)
  }
}

/** 요보 전용 설정 API 사용 가능 여부 (센터장·사회복지사는 false) */
const canUseCaregiverNotifySetting = async (): Promise<boolean> => {
  try {
    const { authStore } = await import('@/stores/auth')
    if (authStore.state.user?.appRole !== 'CAREGIVER') return false
    const res = await appApi.get('/settings')
    return !!res.success
  } catch {
    return false
  }
}

export type EffectiveNotifyReason =
  | 'ok'
  | 'unsupported'
  | 'denied'
  | 'default'
  | 'os-blocked'
  | 'need-pwa'
  | 'inapp'

export type EffectiveNotifyState = {
  granted: boolean
  reason: EffectiveNotifyReason
  permission: NotificationPermission | 'unsupported'
}

const isIosDevice = () =>
  typeof navigator !== 'undefined' && /iPad|iPhone|iPod/i.test(navigator.userAgent || '')

/** 브라우저/OS 알림 권한 (동기, 사이트 권한만) */
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (typeof Notification === 'undefined') return 'unsupported'
  return Notification.permission
}

/**
 * 사이트 권한 + 휴대폰 설정(permissions.query)을 같이 봄.
 * Android는 Notification.permission 이 granted 여도
 * 휴대폰 설정에서 알림이 꺼지면 query 가 denied 인 경우가 있다.
 */
export const resolveNotifyPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (typeof Notification === 'undefined') return 'unsupported'
  let perm: NotificationPermission | 'unsupported' = Notification.permission
  try {
    const q = await navigator.permissions?.query({ name: 'notifications' as PermissionName })
    if (q?.state === 'denied') return 'denied'
    if (q?.state === 'prompt') return 'default'
    if (q?.state === 'granted') perm = 'granted'
  } catch {
    /* iOS 등 query 미지원 */
  }
  return perm
}

const PROBE_TAG = 'caion-os-probe'

/** 예전에 실행 확인용으로 띄워 둔 빈 알림이 남아 있으면 닫는다. 새로 띄우지는 않는다. */
const dismissProbeNotifications = async () => {
  try {
    const reg = await getPushServiceWorkerRegistration()
    const list = await reg?.getNotifications({ tag: PROBE_TAG })
    list?.forEach((n) => n.close())
  } catch {
    /* 이미 없거나 권한 없음 */
  }
}

/** 이 기기에서 알림이 실제로 켜져 있는지 (스위치·발송 준비 기준) */
export const resetNotifyOsProbe = () => {
  void dismissProbeNotifications()
}

export const getEffectiveNotifyState = async (): Promise<EffectiveNotifyState> => {
  const permission = await resolveNotifyPermission()
  if (permission === 'unsupported') {
    return { granted: false, reason: 'unsupported', permission }
  }
  if (typeof window !== 'undefined' && detectInAppBrowser()) {
    return { granted: false, reason: 'inapp', permission }
  }
  if (isIosDevice() && !isStandaloneDisplay()) {
    return { granted: false, reason: 'need-pwa', permission }
  }
  if (permission === 'denied') {
    return { granted: false, reason: 'denied', permission }
  }
  if (permission !== 'granted') {
    return { granted: false, reason: 'default', permission }
  }
  void dismissProbeNotifications()
  return { granted: true, reason: 'ok', permission }
}

const isAndroidDevice = () =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '')

const notifyAppLabel = () => {
  if (typeof location !== 'undefined' && location.hostname.startsWith('test.')) return '카이온테스트'
  return '카이온'
}

const SOUND_GUIDE_KEY = 'caion_sound_guide_done'

const soundGuidePending = () => {
  try {
    return localStorage.getItem(SOUND_GUIDE_KEY) !== '1'
  } catch {
    return true
  }
}

/** 안내 알림을 한 번 띄우면 홈 배너는 접는다. 설정 화면의 안내는 남긴다. */
export const markNotifySoundGuideDone = () => {
  try {
    localStorage.setItem(SOUND_GUIDE_KEY, '1')
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('caion:notify-permission-changed'))
}

/** 알림창에 안내를 쌓는다. 길게 누르면 그 알림의 소리·진동 설정으로 간다. */
export const showNotifySoundGuide = async (): Promise<{ ok: boolean; message?: string }> => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return { ok: false, message: '알림을 먼저 허용해야 소리·진동 설정으로 안내할 수 있습니다.' }
  }
  const appName = notifyAppLabel()
  const origin = window.location.origin
  try {
    const reg = await getPushServiceWorkerRegistration()
    if (!reg?.showNotification) {
      return { ok: false, message: '이 화면에서는 안내 알림을 띄울 수 없습니다.' }
    }
    await reg.showNotification(`${appName} 소리·진동`, {
      body: '이 알림을 길게 누른 뒤 설정을 열고, 소리와 진동을 켜 주세요.',
      tag: 'caion-sound-guide',
      renotify: true,
      requireInteraction: true,
      silent: false,
      icon: `${origin}/pwa-icons/caion-any-192.png?v=20261028b`,
      badge: `${origin}/pwa-icons/caion-monochrome-192.png?v=20261028b`,
      data: { type: 'sound_guide' },
    })
    markNotifySoundGuideDone()
    return { ok: true }
  } catch (e) {
    console.error('[push] sound guide:', e)
    return { ok: false, message: '안내 알림을 띄우지 못했습니다. 설정 → 애플리케이션에서 알림을 열어 주세요.' }
  }
}

export type NotifySetupTone = 'ok' | 'need' | 'check'

export type NotifySetupItem = {
  id: string
  title: string
  detail: string
  steps: string[]
  tone: NotifySetupTone
}

export type NotifySetupView = {
  /** 막힌 항목이 없음. 배터리 확인만 남은 상태도 포함 */
  ready: boolean
  headline: string
  lead: string
  receives: string[]
  still: string[]
  items: NotifySetupItem[]
  action: 'allow' | 'install' | 'none'
  actionLabel: string
  /** 안드로이드, 알림은 허용됨, 소리·진동 안내를 아직 안 열었음 */
  soundGuide: boolean
  /** 설정 목록에 소리·진동 항목을 보여줄지 */
  soundHint: boolean
}

/**
 * 알림이 오려면 앱을 켜 둘 필요는 없다.
 * 권한·설치·브라우저처럼 확인할 수 있는 항목은 need/ok,
 * 배터리 제한처럼 웹에서 볼 수 없는 항목은 check.
 */
export const getNotifySetupView = async (): Promise<NotifySetupView> => {
  const state = await getEffectiveNotifyState()
  const installed = isStandaloneDisplay()
  const android = isAndroidDevice()
  const ios = isIosDevice()
  const appName = notifyAppLabel()
  const { authStore } = await import('@/stores/auth')
  const role = authStore.state.user?.appRole
  const items: NotifySetupItem[] = []

  if (state.reason === 'inapp') {
    items.push({
      id: 'browser',
      title: ios ? 'Safari에서 열기' : 'Chrome에서 열기',
      detail: ios ? 'Safari에서 열어 주세요.' : '카카오톡 안에서는 알림이 오지 않습니다.',
      steps: [],
      tone: 'need',
    })
  } else if (state.reason === 'unsupported') {
    items.push({
      id: 'support',
      title: 'Chrome에서 열기',
      detail: '이 브라우저에서는 알림을 받을 수 없습니다.',
      steps: [],
      tone: 'need',
    })
  }

  if (ios && state.reason !== 'inapp' && !installed) {
    items.push({
      id: 'install',
      title: '홈 화면에 추가',
      detail: 'Safari 공유 → 홈 화면에 추가',
      steps: [],
      tone: 'need',
    })
  } else if (android && state.reason !== 'inapp' && state.reason !== 'unsupported' && !installed) {
    items.push({
      id: 'install',
      title: '앱으로 설치',
      detail: 'Chrome 메뉴 → 앱 설치 또는 홈 화면에 추가',
      steps: [],
      tone: 'need',
    })
  }

  if (state.reason !== 'unsupported' && state.reason !== 'inapp' && state.reason !== 'need-pwa' && !state.granted) {
    items.push({
      id: 'permission',
      title: '알림 허용',
      detail: state.reason === 'default'
        ? '아래 버튼으로 허용해 주세요.'
        : `설정 → 앱 → ${appName} → 알림`,
      steps: [],
      tone: 'need',
    })
  }

  if (android && state.granted && state.reason !== 'inapp' && state.reason !== 'unsupported') {
    items.push({
      id: 'sound',
      title: '소리와 진동',
      detail: `설정 → 앱 → ${appName} → 알림`,
      steps: [],
      tone: 'check',
    })
  }

  const needs = items.some((item) => item.tone === 'need')
  let headline = '알림을 받을 수 있습니다'
  let lead = role === 'CAREGIVER' ? '채팅, 방문 1시간 전' : '채팅'
  if (needs) {
    headline = '알림이 막혀 있습니다'
    lead = '아래만 해 주세요.'
  } else if (state.reason === 'need-pwa') {
    headline = '홈 화면에 추가해 주세요'
    lead = ''
  }

  let action: NotifySetupView['action'] = 'none'
  let actionLabel = ''
  if (items.some((item) => item.id === 'install' && item.tone === 'need')) {
    action = 'install'
    actionLabel = '앱 설치'
  } else if (state.reason === 'default') {
    action = 'allow'
    actionLabel = '알림 허용'
  }

  const soundHint = items.some((item) => item.id === 'sound')
  const soundGuide = soundHint && soundGuidePending()
  return {
    ready: !needs,
    headline,
    lead,
    receives: [],
    still: [],
    items,
    action,
    actionLabel,
    soundGuide,
    soundHint,
  }
}

export const notifySwitchDescription = (reason: EffectiveNotifyReason): string => {
  if (reason === 'ok') return '켜져 있습니다.'
  if (reason === 'denied' || reason === 'os-blocked') return '휴대폰 설정에서 알림을 켜 주세요.'
  if (reason === 'need-pwa') return '홈 화면에 추가해 주세요.'
  if (reason === 'inapp') return 'Chrome에서 열어 주세요.'
  if (reason === 'unsupported') return '이 기기에서는 알림을 쓸 수 없습니다.'
  return '스위치를 켜 주세요.'
}

/** 이 기기에서 OS 알림이 실제로 허용됐는지 (동기, 사이트 권한만) */
export const isDeviceNotifyGranted = (): boolean => getNotificationPermission() === 'granted'

/**
 * 알림 스위치 표시값 — 동기 추정.
 * 실제 UI는 getEffectiveNotifyState() 결과를 쓴다.
 */
export const isNotifySwitchOn = (): boolean => isDeviceNotifyGranted()

let permWatchBound = false
export const bindNotifyPermissionWatch = () => {
  if (permWatchBound || typeof navigator === 'undefined') return
  if (!navigator.permissions?.query) return
  permWatchBound = true
  void navigator.permissions
    .query({ name: 'notifications' as PermissionName })
    .then((status) => {
      status.addEventListener('change', () => {
        probeCache = null
        window.dispatchEvent(new CustomEvent('caion:notify-permission-changed'))
      })
    })
    .catch(() => {
      permWatchBound = false
    })
}

/**
 * 휴대폰 권한에 맞춰 DB·토큰 동기화
 * - 이 기기에서 실제로 알림이 켜진 경우만 DB Y + 토큰 등록
 * - 꺼진 경우 UI만 OFF. DB/토큰은 자동으로 끄지 않음 (다른 기기 푸시 유지)
 */
export const syncNotifyStateWithDevice = async (): Promise<void> => {
  try {
    if (typeof Notification === 'undefined' || !(await isSupported())) return
    bindNotifyPermissionWatch()
    const state = await getEffectiveNotifyState()
    if (!state.granted) return
    if (await canUseCaregiverNotifySetting()) {
      await saveNotifyAllSetting(true)
    }
    await registerPushDevice({ askPermission: false })
  } catch (e) {
    console.error('[push] syncNotifyStateWithDevice:', e)
  }
}

/** DB에서 모든 알림 on/off (요보 전용, 센터는 항상 true — /settings 호출 안 함) */
export const fetchNotifyAllEnabled = async (): Promise<boolean> => {
  try {
    const state = await getEffectiveNotifyState()
    if (!state.granted) return false
    const { authStore } = await import('@/stores/auth')
    if (authStore.state.user?.appRole !== 'CAREGIVER') {
      return true
    }
    const res = await appApi.get('/settings')
    if (!res.success) return true
    const items = (res.data?.items || []) as { code: string; value: string }[]
    const found = items.find((i) => i.code === SETTING_NOTIFY_ALL)
    if (!found) return true
    return found.value === 'Y'
  } catch {
    const state = await getEffectiveNotifyState().catch(() => ({ granted: false }))
    return !!state.granted
  }
}

export const saveNotifyAllSetting = async (enabled: boolean) => {
  const res = await appApi.put('/settings', {
    code: SETTING_NOTIFY_ALL,
    value: enabled ? 'Y' : 'N',
  })
  if (!res.success) throw new Error(res.message || '설정 저장 실패')
  return res.data
}

/**
 * FCM 토큰 등록
 * @param askPermission true일 때만 기기 권한창 요청 (내정보·최초 제안)
 */
export const registerPushDevice = async (opts?: {
  askPermission?: boolean
}): Promise<{ ok: boolean; message?: string }> => {
  try {
    if (typeof window === 'undefined') return { ok: false, message: '앱에서만 사용할 수 있습니다.' }
    if (!(await isSupported())) {
      return { ok: false, message: '이 기기에서는 알림을 사용할 수 없습니다.' }
    }

    await initPushListeners()

    const cfg = await loadConfig()
    if (!cfg) {
      return { ok: false, message: '알림 서비스 설정이 아직 없습니다. 관리자에게 문의하세요.' }
    }

    if (typeof Notification === 'undefined') {
      return { ok: false, message: '이 기기에서는 알림을 사용할 수 없습니다.' }
    }

    let permission = Notification.permission
    if (permission === 'default' && opts?.askPermission) {
      permission = await Notification.requestPermission()
    }
    if (permission !== 'granted') {
      return {
        ok: false,
        message: opts?.askPermission
          ? '기기 알림이 꺼져 있어요. 휴대폰 설정에서 카이온 알림을 켠 뒤 다시 시도해 주세요.'
          : '알림 권한이 없습니다.',
      }
    }

    const effective = await getEffectiveNotifyState()
    if (!effective.granted) {
      return {
        ok: false,
        message:
          effective.reason === 'need-pwa'
            ? '아이폰은 홈 화면에 카이온을 추가해야 알림을 받을 수 있습니다.'
            : '기기 알림이 꺼져 있어요. 휴대폰 설정에서 카이온 알림을 켠 뒤 다시 시도해 주세요.',
      }
    }

    if (!getApps().length) {
      initializeApp({
        apiKey: cfg.apiKey,
        authDomain: cfg.authDomain,
        projectId: cfg.projectId,
        messagingSenderId: cfg.messagingSenderId,
        appId: cfg.appId,
      })
    }

    messagingInstance = getMessaging()
    const swReg = await getPushServiceWorkerRegistration()
    if (!swReg) {
      return { ok: false, message: '알림 연결에 실패했습니다. 잠시 후 다시 시도해 주세요.' }
    }
    const token = await getToken(messagingInstance, {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: swReg,
    })

    if (!token) return { ok: false, message: '알림 연결에 실패했습니다. 잠시 후 다시 시도해 주세요.' }

    const reg = await appApi.post('/push/register', {
      token,
      platform: 'web',
      origin: window.location.origin,
      deviceInfo: navigator.userAgent?.substring(0, 250) || null,
    })
    if (!reg.success) throw new Error(reg.message || '알림 등록 실패')

    saveTokenLocal(token)
    markNotifyOnboarded()
    bindForeground()
    return { ok: true }
  } catch (e: any) {
    console.error('푸시 등록 오류:', e)
    return { ok: false, message: apiErrorMessage(e, '알림 등록에 실패했습니다.') }
  }
}

/**
 * 푸시 기기 등록 해제.
 * @param accessToken 로그아웃 직후처럼 로컬 세션이 이미 지워진 경우, 미리 확보한 Bearer
 */
export const unregisterPushDevice = async (accessToken?: string | null): Promise<void> => {
  const fcm = readTokenLocal()
  try {
    if (fcm) {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      const bearer = String(accessToken || '').trim()
      if (bearer) headers.Authorization = `Bearer ${bearer}`
      // appApi 대신 raw 호출 — 로그아웃 후 인터셉터·세션에 의존하지 않음
      await axios.delete(`/api/app/push/register?token=${encodeURIComponent(fcm)}`, {
        timeout: 8000,
        headers,
      })
    }
  } catch (e) {
    console.error('푸시 해제 오류:', e)
  } finally {
    clearTokenLocal()
  }
}

export type LaunchPushResult = {
  ok: boolean
  /** 알림 허용 제안 모달을 띄워야 함 */
  shouldPropose?: boolean
  /** 제안 사유 — 문구 분기 */
  proposeReason?: NotifyProposeReason
  message?: string
}

/**
 * 앱 실행 시 호출 — 권한창을 띄우지 않음
 * - 이미 허용 + DB ON → 조용히 토큰 동기화
 * - 권한 미결정 + (최초 / 설치직후 / 업데이트) → shouldPropose
 */
export const syncPushOnLaunch = async (): Promise<LaunchPushResult> => {
  try {
    await dismissProbeNotifications()
    await initPushListeners()

    if (typeof Notification === 'undefined' || !(await isSupported())) {
      return { ok: false, message: '이 기기에서는 알림을 사용할 수 없습니다.' }
    }

    const versionReason = applyNotifyPromptVersionPolicy()
    const pendingInstall = hasPendingNotifyAfterInstall()
    const device = await getEffectiveNotifyState()
    const enabled = await fetchNotifyAllEnabled()

    // 기기 권한 미결정 → DB가 OFF여도 제안 (허용 시 DB도 ON)
    // ※ 「나중에」로 onboarded 된 뒤에는 재묻지 않음 (토큰 없음만으로 재제안 금지)
    if (device.reason === 'default') {
      const needsPropose =
        pendingInstall || versionReason === 'update' || !isNotifyOnboarded()
      if (needsPropose) {
        const proposeReason: LaunchPushResult['proposeReason'] = pendingInstall
          ? 'install'
          : versionReason === 'update'
            ? 'update'
            : 'onboard'
        return { ok: false, shouldPropose: true, proposeReason }
      }
      return { ok: false, message: '알림 권한이 없습니다.' }
    }

    // 이미 허용/거절된 경우 설치 대기 플래그만 정리
    if (pendingInstall) clearPendingNotifyAfterInstall()

    if (!device.granted) {
      return { ok: false, message: '알림 권한이 없습니다.' }
    }

    // OS 허용됨 → DB도 ON으로 맞춘 뒤 토큰 등록
    // (예전에 「나중에」로 DB만 OFF인 채 권한만 켜진 상태 복구)
    if (!enabled && (await canUseCaregiverNotifySetting())) {
      try {
        await saveNotifyAllSetting(true)
      } catch (e) {
        console.warn('[push] launch DB sync:', e)
      }
    }

    return registerPushDevice({ askPermission: false })
  } catch (e: any) {
    return { ok: false, message: apiErrorMessage(e, '알림 준비에 실패했습니다.') }
  }
}

/** @deprecated syncPushOnLaunch 사용 */
export const setupSchedulePush = syncPushOnLaunch

/** 최초 제안 — 허용 */
export const acceptNotifyOnboarding = async (): Promise<{ ok: boolean; message?: string }> => {
  markNotifyOnboarded()
  clearPendingNotifyAfterInstall()
  return enableSchedulePush()
}

/** 최초 제안 — 나중에(해제) */
export const declineNotifyOnboarding = async (): Promise<{ ok: boolean; message?: string }> => {
  markNotifyOnboarded()
  clearPendingNotifyAfterInstall()
  try {
    if (await canUseCaregiverNotifySetting()) {
      await saveNotifyAllSetting(false)
      await unregisterPushDevice()
    }
    return { ok: true }
  } catch (e: any) {
    return { ok: false, message: apiErrorMessage(e, '설정 저장에 실패했습니다.') }
  }
}

/** 내정보 — 모든 알림 허용 (이때만 권한창) */
export const enableSchedulePush = async (): Promise<{ ok: boolean; message?: string }> => {
  const device = await registerPushDevice({ askPermission: true })
  if (!device.ok) return device
  try {
    if (await canUseCaregiverNotifySetting()) {
      await saveNotifyAllSetting(true)
    }
    markNotifyOnboarded()
    return { ok: true }
  } catch (e: any) {
    return { ok: false, message: apiErrorMessage(e, '설정 저장에 실패했습니다.') }
  }
}

/** 내정보 — 모든 알림 해제 */
export const disableSchedulePush = async (): Promise<{ ok: boolean; message?: string }> => {
  try {
    await saveNotifyAllSetting(false)
    await unregisterPushDevice()
    markNotifyOnboarded()
    return { ok: true, message: '모든 알림을 해제했습니다.' }
  } catch (e: any) {
    return { ok: false, message: apiErrorMessage(e, '설정 저장에 실패했습니다.') }
  }
}

export const getRegisteredPushToken = () => readTokenLocal()

/**
 * 알림 권한이 이미 허용된 경우 토큰만 조용히 재등록 (센터장·요보 공통)
 */
export const ensurePushRegistered = async (): Promise<{ ok: boolean; message?: string }> => {
  if (typeof Notification === 'undefined' || !(await isSupported())) {
    return { ok: false, message: '이 기기에서는 알림을 사용할 수 없습니다.' }
  }
  const state = await getEffectiveNotifyState()
  if (!state.granted) {
    return { ok: false, message: '알림 권한이 없습니다.' }
  }
  try {
    await syncNotifyStateWithDevice()
    return { ok: true }
  } catch (e: any) {
    return { ok: false, message: apiErrorMessage(e, '알림 등록에 실패했습니다.') }
  }
}

/** 로그인·채팅 진입 등 — 권한 있으면 DB·토큰 즉시 동기화 */
export const syncPushAfterAuth = async (): Promise<void> => {
  try {
    await initPushListeners()
    if (typeof Notification === 'undefined' || !(await isSupported())) return
    const state = await getEffectiveNotifyState()
    if (state.granted) {
      await syncNotifyStateWithDevice()
    }
  } catch (e) {
    console.error('[push] syncPushAfterAuth:', e)
  }
}

/** 채팅 탭 — 서버 미등록·권한 미결정이면 다시 제안 (최초 「나중에」 이후에도) */
export const prepareChatPush = async (opts?: {
  confirm: (p: { title: string; message: string; confirmText: string; cancelText: string }) => Promise<boolean>
}): Promise<void> => {
  await initPushListeners()
  if (typeof Notification === 'undefined' || !(await isSupported())) return

  const device = await getEffectiveNotifyState()
  if (device.granted) {
    await syncNotifyStateWithDevice()
    return
  }

  if (device.reason !== 'default' || !opts?.confirm) return

  const registered = await fetchPushRegistered()
  if (registered) return

  const ok = await opts.confirm({
    title: '채팅 알림',
    message: '새 메시지를 휴대폰으로 받으려면 알림을 허용해 주세요.',
    confirmText: '허용',
    cancelText: '나중에',
  })
  if (!ok) return

  markNotifyOnboarded()
  const r = await enableSchedulePush()
  if (!r.ok && r.message) console.info('[push]', r.message)
}

export const fetchPushRegistered = async (): Promise<boolean> => {
  try {
    const res = await appApi.get<{ registered?: boolean }>('/push/status')
    return !!res.success && !!res.data?.registered
  } catch {
    return false
  }
}
